import React, { FC, useCallback, useEffect, useState } from 'react';
import { Chip, FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import Employee from '../../interfaces/api/Employee';
import useEmployeeAccess from '../../hooks/api/useEmployeeAccess';
import { createUseStyles } from '../../theming';

interface Props {
  error?: boolean;
  helperText?: string;
  className?: any;
  label?: string;
  fullWidth?: boolean;
  placeholder?: string;
  size?: 'small' | 'medium';
  onChange: (value?: string | string[]) => void;
}

interface SelectProps {
  value?: string;
  multiple?: false;
  onChange: (value?: string) => void;
}

interface MultiSelectProps {
  value: string[];
  multiple: true;
  onChange: (value?: string[]) => void;
}

type EmployeeSelectProps = Props & (SelectProps | MultiSelectProps);

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const useStyles = createUseStyles({
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chip: {
    margin: 2,
  },
});

const EmployeeSelect: FC<EmployeeSelectProps> = React.forwardRef<any, EmployeeSelectProps>(
  ({ onChange, multiple, placeholder, label, value, className, helperText, ...props }, ref) => {
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();
    const classes = useStyles();

    const { getAll } = useEmployeeAccess();

    const getEmployee = useCallback((employeeId: string) => employees.find((employee) => employee.id === employeeId), [
      employees,
    ]);

    const getEmployeeLabel = useCallback(
      (employeeId: string) => {
        const employee = getEmployee(employeeId);
        return `${employee?.firstname} ${employee?.lastname}`;
      },
      [employees]
    );

    useEffect(() => {
      getAll()
        .then((collection) => {
          setEmployees(collection.items);
          setLoading(false);
        })
        .catch((e) => {
          setError(e.message);
          setLoading(false);
        });
    }, []);

    const handleSortEmployee = (employeeA: Employee, employeeB: Employee) => {
      return `${employeeA.firstname} ${employeeA.lastname}`.localeCompare(
        `${employeeB.firstname} ${employeeB.lastname}`
      );
    };

    const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
      const newValue = event.target.value;

      if (multiple) {
        onChange(newValue as string[]);
      } else {
        onChange(newValue as string);
      }
    };

    if (loading) {
      return <Skeleton height={50} />;
    }

    if (error) {
      return <div style={{ color: '#f44336' }}>{error}</div>;
    }

    return (
      <FormControl className={className} ref={ref} variant='outlined' {...props}>
        <InputLabel>{label || 'Employé' + (multiple ? '(s)' : '')}</InputLabel>
        <Select
          value={value || ''}
          multiple={multiple}
          label={label || 'Employé' + (multiple ? '(s)' : '')}
          onChange={handleChange}
          MenuProps={MenuProps}
          renderValue={(selected) =>
            multiple ? (
              <div className={classes.chips}>
                {(selected as string[]).map((value) => (
                  <Chip key={value} label={getEmployeeLabel(value)} className={classes.chip} />
                ))}
              </div>
            ) : (
              <div className={classes.chips}>{getEmployeeLabel(selected as string)}</div>
            )
          }
        >
          {!multiple && <MenuItem value=''>{placeholder || ''}</MenuItem>}
          {employees.sort(handleSortEmployee).map((employee) => (
            <MenuItem key={employee.id} value={employee.id}>
              {getEmployeeLabel(employee.id)}
            </MenuItem>
          ))}
        </Select>

        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }
);

export default React.memo(EmployeeSelect);
